import type { Session } from './api';

export interface RouteEntry {
  /** Unique route identifier */
  readonly id: string;
  /** Hash fragment ('' for default/operations) */
  readonly hash: string;
  /** Display label in sidebar and breadcrumbs */
  readonly label: string;
  /** Permission required to see this nav item (null = always visible) */
  readonly permission: string | null;
  /** Sidebar section */
  readonly section: 'main' | 'bottom';
  /** Navigation group — routes with the same group render under a shared heading */
  readonly group?: 'work' | 'reports';
  /** Whether this route also matches sub-routes (e.g. 'clients' matches 'client/:id') */
  readonly matchPrefix?: string;
  /** Icon key used by the Sidebar component */
  readonly icon?: string;
  /** If set, the route is only visible to users whose role is in this list. */
  readonly visibleToRoles?: readonly string[];
}

export const ROUTES: readonly RouteEntry[] = [
  // ── Self-service (recruiter/salesperson — top-level, no group) ──
  {
    id: 'my-commissions',
    hash: 'my-commissions',
    label: 'My Commissions',
    permission: 'commission:read:own',
    section: 'main',
    icon: 'my-commissions',
    visibleToRoles: ['recruiter', 'salesperson'],
  },

  // ── Work group (operations/admin) ──
  {
    id: 'operations',
    hash: '',
    label: 'Operations',
    permission: 'financials:read',
    section: 'main',
    group: 'work',
    icon: 'operations',
  },
  {
    id: 'clients',
    hash: 'clients',
    label: 'Clients',
    permission: 'financials:read',
    section: 'main',
    group: 'work',
    matchPrefix: 'client/',
    icon: 'clients',
  },
  {
    id: 'contractors',
    hash: 'contractors',
    label: 'Contractors',
    permission: 'financials:read',
    section: 'main',
    group: 'work',
    matchPrefix: 'contractor/',
    icon: 'contractors',
  },
  {
    id: 'recruiters',
    hash: 'recruiters',
    label: 'Commission Earners',
    permission: 'commission:read:all',
    section: 'main',
    group: 'work',
    matchPrefix: 'recruiter/',
    icon: 'recruiters',
  },
  {
    id: 'credit-assignments',
    hash: 'credit-assignments',
    label: 'Commission Credits',
    permission: 'commission:read:all',
    section: 'main',
    group: 'work',
    icon: 'credits',
  },

  // ── Reports group ──
  {
    id: 'gp-summary',
    hash: 'gp-summary',
    label: 'GP Summary',
    permission: 'financials:read',
    section: 'main',
    group: 'reports',
    icon: 'gp-summary',
  },
  {
    id: 'weekly-commission',
    hash: 'weekly-commission',
    label: 'Commission Report',
    permission: 'commission:read:all',
    section: 'main',
    group: 'reports',
    icon: 'commission-report',
  },
  {
    id: 'weekly-scoreboard',
    hash: 'weekly-scoreboard',
    label: 'Company Goals',
    permission: 'leaderboard:read',
    section: 'main',
    group: 'reports',
    icon: 'scoreboard',
  },
  {
    id: 'expenses-report',
    hash: 'expenses-report',
    label: 'Expenses',
    permission: 'financials:read',
    section: 'main',
    group: 'reports',
    icon: 'expenses',
  },
  {
    id: 'adp-preview',
    hash: 'adp-preview',
    label: 'ADP Preview',
    permission: 'financials:read',
    section: 'main',
    group: 'reports',
    icon: 'payroll',
  },

  // ── Standalone (no group) ──
  {
    id: 'executive',
    hash: 'executive',
    label: 'Executive Dashboard',
    permission: 'financials:read',
    section: 'main',
    icon: 'executive',
  },

  // ── Bottom section ──
  {
    id: 'docs',
    hash: 'docs',
    label: 'Documentation',
    permission: 'users:manage',
    section: 'bottom',
    icon: 'docs',
  },
  {
    id: 'admin',
    hash: 'admin',
    label: 'Admin',
    permission: 'config:write',
    section: 'bottom',
    icon: 'admin',
  },
];

/** Group labels for sidebar rendering */
export const GROUP_LABELS: Record<string, string> = {
  work: 'Work',
  reports: 'Reports',
};

/** Find the route entry matching a parsed page id */
export function findRoute(pageId: string): RouteEntry | undefined {
  return ROUTES.find((r) => r.id === pageId);
}

/** Check if a route is visible to the given session */
export function isRouteVisible(route: RouteEntry, session: Session): boolean {
  if (route.permission !== null && !session.permissions.includes(route.permission)) {
    return false;
  }
  if (route.visibleToRoles && !route.visibleToRoles.includes(session.user.role)) {
    return false;
  }
  return true;
}

/** Get the active route id for the current hash, accounting for sub-routes */
export function activeRouteId(pageId: string): string {
  if (pageId === 'client') return 'clients';
  if (pageId === 'recruiter') return 'recruiters';
  if (pageId === 'contractor') return 'contractors';
  return pageId;
}
